import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '@mui/material/Button';
import { GridColDef } from '@mui/x-data-grid-pro';

import DataGridTable from '@/shared/components/dataDisplay/DataGridTable/DataGridTable';
import TableInstance from '@/shared/constants/tableInstance';
import BookingsService from '@/shared/types/bookings/service.type';

import useBookingServiceTable_v2 from './hooks/useBookingServiceTable_v2';

type BookingServiceTable_v2Props = {
  refreshTableIndex: number;
  setBookingServiceEditDialog: (
    open: boolean,
    bookingService: BookingsService,
  ) => void;
  setBookingServiceAddContactsDialog: (
    open: boolean,
    bookingService: BookingsService,
  ) => void;
  setBookingServiceRemoveContactsDialog: (
    open: boolean,
    bookingService: BookingsService,
  ) => void;
  setCreateBookingServiceDialog: (open: boolean) => void;
};

const BookingServiceTable_v2: FC<BookingServiceTable_v2Props> = ({
  refreshTableIndex,
  setBookingServiceEditDialog,
  setBookingServiceAddContactsDialog,
  setBookingServiceRemoveContactsDialog,
  setCreateBookingServiceDialog,
}) => {
  const { t } = useTranslation(['bookingsServices', 'common']);

  const { apiRef, model, table, columns, visibilityModel, setVisibilityModel } =
    useBookingServiceTable_v2({ refreshTableIndex });

  const customizedColumns: GridColDef[] = columns.map((column) => {
    switch (column.field) {
      case 'assignedCalendars':
        return {
          ...column,
          filterable: false,
          renderCell: (params) => {
            const row = params.row as BookingsService;
            return row.contacts.length;
          },
        };
      default:
        return column;
    }
  });

  return (
    <div style={{ height: '80vh', width: '100%' }}>
      <DataGridTable
        getRowId={(item) => item.booking_service_id as string}
        columns={customizedColumns}
        rows={table.data}
        rowCount={table.totalRows}
        loading={table.isLoading}
        columnVisibilityModel={visibilityModel}
        onColumnVisibilityModelChange={setVisibilityModel}
        checkboxSelection={false}
        customProps={{
          tableInstance: TableInstance.BookingsServices,
          apiRef,
          model,
          toolbar: {
            toolbarActions: (
              <>
                <Button
                  variant='contained'
                  size='large'
                  onClick={() => setCreateBookingServiceDialog(true)}
                >
                  {t('bookingsServices:createBookingService')}
                </Button>
              </>
            ),
            rowActions: [
              {
                type: 'default',
                button: {
                  label: t('bookingsServices:addContact'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as BookingsService;
                    setBookingServiceAddContactsDialog(true, selectedRow);
                  },
                },
              },
              {
                type: 'default',
                button: {
                  label: t('bookingsServices:updateContact'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as BookingsService;
                    setBookingServiceEditDialog(true, selectedRow);
                  },
                },
              },
              {
                type: 'default',
                button: {
                  label: t('bookingsServices:deleteContact'),
                  onClick: (apiRef, rowId) => {
                    const selectedRow = apiRef.current.getRow(
                      rowId || '',
                    ) as BookingsService;
                    setBookingServiceRemoveContactsDialog(true, selectedRow);
                  },
                },
              },
            ],
          },
        }}
      />
    </div>
  );
};

export default BookingServiceTable_v2;
